import {
  SwitchField,
  Modal,
  Button,
  Callout,
  Link,
} from '@dev-spendesk/grapes';
import React from 'react';
import { Trans } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useNotifications, NotificationType } from 'modules/app/notifications';
import { useFeature } from 'src/core/common/hooks/useFeature';
import {
  type I18nKey,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routes, routeFor } from 'src/core/constants/routes';
import { useGoToBillingPage } from 'src/core/modules/billing/hooks/useGoToBillingPage';
import { track, AnalyticEventName } from 'src/core/utils/analytics';
import { logger } from 'src/utils/datadog-log-wrapper';

import { useDisableFeature } from '../../hooks/useDisableFeature';
import { useEnableFeature } from '../../hooks/useEnableFeature';
import type { SpendingMethod as SpendingMethodType } from '../../spendingMethods';

type EnabledFeatures = {
  isCostCenterActivated: boolean;
  isPurchaseOrderEnabled: boolean;
};

function getTranslationKeyFromFeatureCode(
  featureCode: SpendingMethodType,
  { isCostCenterActivated, isPurchaseOrderEnabled }: EnabledFeatures,
): {
  titleKey: I18nKey;
  descriptionKey: I18nKey;
  activationSubtitleKey: I18nKey;
  deactivationSubtitleKey: I18nKey;
  warningKey?: I18nKey;
} {
  const defaults = {
    activationSubtitleKey:
      'payments.settings.modal.activation.subtitle' as const,
    deactivationSubtitleKey:
      'payments.settings.modal.deactivation.subtitle' as const,
  };
  switch (featureCode) {
    default:
    case FEATURES.EXPENSE_CLAIMS:
      return {
        titleKey: 'payments.settings.expenseClaims.title',
        descriptionKey: 'payments.settings.expenseClaims.description',
        ...defaults,
      };
    case FEATURES.SUBMIT_MY_INVOICE:
      return {
        titleKey: 'payments.settings.invoices.title',
        descriptionKey: 'payments.settings.invoices.description',
        warningKey: 'payments.settings.invoices.warning',
        ...defaults,
      };
    case FEATURES.PURCHASE_ORDERS_ACTIVATED:
      return {
        titleKey: 'payments.settings.purchaseOrders.title',
        descriptionKey: 'payments.settings.purchaseOrders.description',
        warningKey: isCostCenterActivated
          ? 'payments.settings.purchaseOrders.warning_invoice_disabled'
          : 'payments.settings.purchaseOrders.warning',
        ...defaults,
      };
    case FEATURES.DELIVERY_NOTES_FEATURE_ACTIVATED:
      return {
        titleKey: 'payments.settings.deliveryNotes.title',
        descriptionKey: 'payments.settings.deliveryNotes.description',
        warningKey: isPurchaseOrderEnabled
          ? undefined
          : 'payments.settings.deliveryNotes.warning',
        activationSubtitleKey:
          'payments.settings.deliveryNotes.modal.activation.subtitle',
        deactivationSubtitleKey:
          'payments.settings.deliveryNotes.modal.deactivation.subtitle',
      };
    case FEATURES.PER_DIEM_ALLOWANCES:
      return {
        titleKey: 'payments.settings.perDiemAllowances.title',
        descriptionKey: 'payments.settings.perDiemAllowances.description',
        ...defaults,
      };
    case FEATURES.MILEAGE_ALLOWANCES:
      return {
        titleKey: 'payments.settings.mileageAllowances.title',
        descriptionKey: 'payments.settings.mileageAllowances.description',
        warningKey: 'payments.settings.mileageAllowances.warning',
        ...defaults,
      };
  }
}

type Props = {
  featureCode: SpendingMethodType;
  isDisabled: boolean;
};
export const SpendingMethod = ({ featureCode, isDisabled }: Props) => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const { t } = useTranslation('global');

  const companyId = useCompanyId();
  const hasFeature = useFeature(featureCode);
  const isCostCenterActivated = useFeature(FEATURES.COST_CENTERS_ACTIVATED);
  const isPurchaseOrderEnabled = useFeature(FEATURES.PURCHASE_ORDERS_ACTIVATED);
  const isDeliveryNoteEnabled = useFeature(FEATURES.DELIVERY_NOTES_FEATURE);
  const isProcurementEnabled = useFeature(FEATURES.PROCUREMENT);
  const { pushNotif } = useNotifications();

  const [disableFeature] = useDisableFeature(featureCode);
  const [enableFeature] = useEnableFeature();

  async function handlePrimaryAction() {
    setModalOpen(false);
    try {
      if (hasFeature) {
        await disableFeature();
      } else {
        await enableFeature({ featureCode });
      }
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('errors:somethingWrong'),
      });
    }
  }

  const {
    titleKey,
    descriptionKey,
    warningKey,
    activationSubtitleKey,
    deactivationSubtitleKey,
  } = getTranslationKeyFromFeatureCode(featureCode, {
    isCostCenterActivated,
    isPurchaseOrderEnabled,
  });

  const title = t(titleKey);
  const description = t(descriptionKey);

  /**
   * Only used by Purchase order spending method.
   * Unfortunately, route has to be defined for the Link component below
   */
  const route = routeFor(routes.COST_CENTERS.path, {
    company: companyId,
  });

  /**
   * Using the delivery notes feature, we want to entice
   * customers who have purchase orders enabled to the the procurement feature
   */
  const shouldRenderCallToActionForProcurement =
    !isDeliveryNoteEnabled &&
    isPurchaseOrderEnabled &&
    !isProcurementEnabled &&
    featureCode === FEATURES.DELIVERY_NOTES_FEATURE_ACTIVATED;
  const goToBillingPage = useGoToBillingPage();

  return (
    <>
      <SwitchField
        id={featureCode}
        fit="parent"
        isChecked={hasFeature}
        label={title}
        helpText={description}
        isDisabled={isDisabled}
        onChange={() => {
          setModalOpen(true);
          track(
            AnalyticEventName.SETTINGS_PAYMENT_METHODS_SPENDING_METHOD_SWITCH_CLICKED,
            { feature: featureCode, isFeatureEnabled: hasFeature },
          );
        }}
      />
      {isDisabled && warningKey && (
        <Callout
          variant="info"
          title={
            <Trans
              i18nKey={warningKey as string}
              // eslint-disable-next-line react/jsx-key
              components={[<Link as={ReactRouterLink} to={route} />]}
            />
          }
          className="mb-16"
        />
      )}
      {shouldRenderCallToActionForProcurement && (
        <Button
          onClick={() => {
            logger.info('CTA clicked to subscribe to procurement add-on', {
              scope: 'cta:procurement-add-on',
              team: 'invoice-automation',
            });
            goToBillingPage();
          }}
          text={t('payments.settings.deliveryNotes.cta')}
          variant="secondaryNeutral"
          className="mb-8"
        />
      )}
      <Modal
        actions={[
          <Button
            key="no"
            onClick={() => {
              setModalOpen(false);
              track(
                AnalyticEventName.SETTINGS_PAYMENT_METHODS_SPENDING_METHOD_MODAL_CANCEL_BUTTON_CLICKED,
                {
                  feature: featureCode,
                },
              );
            }}
            text={t('misc.cancel')}
            variant="secondaryNeutral"
          />,
          <Button
            key="yes"
            text={
              hasFeature
                ? t('payments.settings.modal.deactivation.action', {
                    payment: title,
                  })
                : t('payments.settings.modal.activation.action', {
                    payment: title,
                  })
            }
            variant="primaryBrand"
            onClick={() => {
              handlePrimaryAction();
              track(
                hasFeature
                  ? AnalyticEventName.SETTINGS_PAYMENT_METHODS_SPENDING_METHOD_MODAL_DEACTIVATE_BUTTON_CLICKED
                  : AnalyticEventName.SETTINGS_PAYMENT_METHODS_SPENDING_METHOD_MODAL_ACTIVATE_BUTTON_CLICKED,
                {
                  feature: featureCode,
                },
              );
            }}
          />,
        ]}
        onClose={() => {
          setModalOpen(false);
          track(
            AnalyticEventName.SETTINGS_PAYMENT_METHODS_SPENDING_METHOD_MODAL_CANCEL_BUTTON_CLICKED,
            {
              feature: featureCode,
            },
          );
        }}
        iconName="lock-close"
        iconVariant="info"
        title={
          hasFeature
            ? t('payments.settings.modal.deactivation.title', {
                payment: title,
              })
            : t('payments.settings.modal.activation.title', { payment: title })
        }
        subtitle={
          hasFeature
            ? t(deactivationSubtitleKey, { payment: title })
            : t(activationSubtitleKey, { payment: title })
        }
        isOpen={isModalOpen}
      />
    </>
  );
};
